/**
 * One time of the plan (the window, the layout time, the morning summary):
 * its label (with an optional hint), the picker on the DANISH clock with
 * "−1d" when that is Denmark's evening before, and the country's own time
 * underneath (user, 19/10/2026: "always select Danish time, and the local
 * time under it"). The value in and out stays the country's time — these
 * are its rules — converted exactly for the day shown. `inline` puts the
 * label before the picker, for a time inside a sentence. `nextDay` marks a
 * time on the following day — the window's close past midnight ("+1 day",
 * 26/10/2026, client: "Allow window to close following day +1").
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';

import { TimeIntervalPicker } from '@/components/ui';

import { dkOfLocal, localOfDk } from '../../../utils/clock/dk-input';
import { shiftMark } from '../../../utils/clock/dk-label';
import { useCountryClock } from '../../shared/time/country-clock-context';

interface Props {
  label: ReactNode;
  /** The country's time, HH:MM. */
  value: string;
  /** Gets the country's time, HH:MM. */
  onChange: (hhmm: string) => void;
  recipientTimezone: string | null;
  inline?: boolean;
  /** The time falls on the next day (a window closing past midnight). */
  nextDay?: boolean;
}

const ECHO = 'font-mono text-[11px] tabular-nums text-muted-foreground';

const DanishPicker: FC<Omit<Props, 'label' | 'inline'>> = ({ value, onChange, recipientTimezone: tz, nextDay = false }) => {
  const { runDate } = useCountryClock();
  const dk = tz ? dkOfLocal(value, tz, runDate) : null;
  const pick = (v: string) => {
    const local = tz ? localOfDk(v, tz, runDate) : v;
    if (local !== null) onChange(local);
  };
  return (
    <span className="flex items-center gap-1.5">
      <span aria-hidden title="Danish time">🇩🇰</span>
      <TimeIntervalPicker value={dk?.hhmm ?? value} onChange={pick} />
      {dk && dk.shift !== 0 ? (
        <span className={ECHO} title={dk.shift < 0 ? 'The evening before, in Denmark' : 'The next day, in Denmark'} data-testid="dk-day-shift">
          {shiftMark(dk.shift).trim()}
        </span>
      ) : null}
      {nextDay ? (
        <span className="rounded-full bg-primary/10 px-1.5 py-0.5 font-mono text-[10.5px] font-semibold tabular-nums text-primary" title="The next day — the window runs past midnight" data-testid="next-day">
          +1 day
        </span>
      ) : null}
    </span>
  );
};

const CountryTime: FC<{ value: string; tz: string | null }> = ({ value, tz }) => {
  const { flag } = useCountryClock();
  return (
    <span
      className={ECHO}
      title={tz ? "The country's own time — what is saved; it stays put when either country changes its clocks" : "Set the country's timezone to see its own time"}
      data-testid="country-echo"
    >
      {flag} {tz ? value : '—'}
    </span>
  );
};

export const TimeField: FC<Props> = ({ label, inline = false, ...field }) => {
  const body = (
    <>
      <DanishPicker {...field} />
      <CountryTime value={field.value} tz={field.recipientTimezone} />
    </>
  );
  if (inline) {
    return (
      <span className="inline-flex items-baseline gap-1.5">
        <span className="text-muted-foreground">{label}</span>
        <span className="flex flex-col gap-1">{body}</span>
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {body}
    </div>
  );
};
